import {
    type ActionReducerMapBuilder,
    createAsyncThunk,
    createSlice,
    type Draft,
    type SerializedError,
} from '@reduxjs/toolkit';

import type { IAction, IGlobalStoreReducers } from './ReduxStore';
import initI18n from '../locales';
import { GlobalVariables } from '../pdf/GlobalVariables';
import { backendApiUrl } from '../App';
import { getLicenseKey } from '../utils/stringUtils';

export interface IInitSettings {
    readonly lic: string;
    readonly documentUrl: string;
    readonly language: string;
    readonly testRun?: boolean;
    readonly customFontURL?: string;
}

export interface IInitSettingsState {
    loading: boolean;
    data: IInitSettings;
    error: SerializedError | null;
}

const initialState: IInitSettingsState = {
    loading: false,
    data: {
        customFontURL: process.env.REACT_APP_FONTS_URL ?? '',
        documentUrl: process.env.REACT_APP_DOC_URL ?? '',
        lic: getLicenseKey(),
        language: navigator.language || 'en',
    },
    error: null,
};

const thunks = {
    /**
     * Logs the user out of the application.
     */
    getInitSettings: async (): Promise<IInitSettings> => {
        try {
            if (GlobalVariables.isVisualStudioCode) {
                return new Promise<IInitSettings>(resolve => {
                    setTimeout(() => {
                        resolve({
                            customFontURL: process.env.REACT_APP_FONTS_URL ?? '',
                            documentUrl: process.env.REACT_APP_DOC_URL ?? '',
                            lic: getLicenseKey(),
                            language: navigator.language || 'en',
                        });
                    }, 250);
                });
            }

            const response = await fetch(backendApiUrl.init);

            const initSettings: IInitSettings = await response.json();

            initI18n(initSettings.language);

            return response.ok ? Promise.resolve(initSettings) : Promise.reject(response.status);
        } catch (e) {
            return Promise.reject(e);
        }
    },

    _reducers: (builder: ActionReducerMapBuilder<IInitSettingsState>) => {
        builder.addCase(getInitSettings.pending, (state: Draft<IInitSettingsState>) => {
            state.error = null;
            state.loading = true;
        });
        builder.addCase(
            getInitSettings.fulfilled,
            (state: Draft<IInitSettingsState>, action: IAction<IInitSettings>) => {
                state.data = action.payload;
                state.error = null;
                state.loading = false;
            },
        );
        builder.addCase(getInitSettings.rejected, (state, action) => {
            state.error = action.error;
            state.loading = false;
        });
    },
};

export const getInitSettings = createAsyncThunk('init/settings', thunks.getInitSettings);

export const initSettingsSlice = createSlice({
    name: 'initSettings',
    initialState,
    reducers: {},
    extraReducers: thunks._reducers,
});

export const initSettingsError = (state: IGlobalStoreReducers) => state.initSettings.error;

export default initSettingsSlice.reducer;
